import React from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { Home, PlusCircle, List, FileText, MoreHorizontal, Wallet, Upload, Tags, LogOut, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const primaryNav = [
  { to: "/", label: "Início", icon: Home, testid: "nav-inicio", end: true },
  { to: "/registar", label: "Registar", icon: PlusCircle, testid: "nav-registar" },
  { to: "/historico", label: "Histórico", icon: List, testid: "nav-historico" },
  { to: "/faturas", label: "Faturas", icon: FileText, testid: "nav-faturas" },
];

const moreNav = [
  { to: "/deteccoes", label: "Deteções (email)", icon: Mail, testid: "nav-deteccoes" },
  { to: "/contas", label: "Contas", icon: Wallet, testid: "nav-contas" },
  { to: "/importar", label: "Importar", icon: Upload, testid: "nav-importar" },
  { to: "/regras", label: "Regras & Categorias", icon: Tags, testid: "nav-regras" },
];

export default function Layout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const linkBase = "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors";

  return (
    <div className="App min-h-screen bg-background">
      {/* Header desktop */}
      <header className="hidden md:flex sticky top-0 z-30 items-center justify-between px-8 h-16 bg-card border-b border-border">
        <div className="flex items-center gap-8">
          <div className="font-heading text-xl font-extrabold text-primary tracking-tight" data-testid="brand">
            Cash<span className="text-[#C75D42]">.</span>
          </div>
          <nav className="flex items-center gap-1">
            {[...primaryNav, ...moreNav].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`${n.testid}-desktop`}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/60"}`
                }
              >
                <n.icon size={17} /> {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn-desktop"
          className={`${linkBase} text-muted-foreground hover:bg-secondary/60`}
        >
          <LogOut size={17} /> Terminar sessão
        </button>
      </header>

      {/* Header mobile */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-card border-b border-border">
        <div className="font-heading text-lg font-extrabold text-primary" data-testid="brand-mobile">
          Cash<span className="text-[#C75D42]">.</span>
        </div>
        <span className="text-xs text-muted-foreground truncate max-w-[150px]">{user?.email}</span>
      </header>

      <main className="p-4 md:p-8 pb-28 md:pb-8 max-w-6xl mx-auto w-full">
        <Outlet />
      </main>

      {/* Bottom nav mobile */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border grid grid-cols-5 h-16"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {primaryNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`${n.testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[11px] ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <n.icon size={20} />
            {n.label}
          </NavLink>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="nav-mais-mobile"
              className={`flex flex-col items-center justify-center gap-0.5 text-[11px] ${
                moreNav.some((m) => location.pathname.startsWith(m.to)) ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <MoreHorizontal size={20} />
              Mais
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="mb-2">
            {moreNav.map((m) => (
              <DropdownMenuItem key={m.to} onClick={() => navigate(m.to)} data-testid={`${m.testid}-mobile`}>
                <m.icon size={16} className="mr-2" /> {m.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={logout} data-testid="logout-btn-mobile">
              <LogOut size={16} className="mr-2" /> Terminar sessão
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    </div>
  );
}
